/**
 * Tu plan — contratar y gestionar lo contratado (`OD-0014` Enmienda A, A-10 y
 * A-11).
 *
 * ## Lo que esta pantalla NO hace, que es lo importante
 *
 * **Volver de Stripe no es haber pagado.** Stripe devuelve el navegador a
 * `/plan?pago=confirmando`, y ese parámetro viaja en la barra de direcciones:
 * lo puede escribir cualquiera. Quien confirma el cobro es el webhook, por
 * detrás, y quien sube el plan es el procesador de eventos.
 *
 * Por eso aquí `pago=confirmando` hace exactamente dos cosas: encender un
 * mensaje y volver a preguntarle a la API. Nunca cambia el plan que se enseña.
 * Si esta pantalla se creyera la URL de vuelta, el plan se concedería desde la
 * barra de direcciones.
 *
 * ## Quién puede
 *
 * Lo decide la API, no esta pantalla (mismo criterio que `PantallaEmisor`):
 * `GET /api/v1/billing/subscription` ya exige OWNER, así que un 403 se enseña
 * como "sin permiso" en vez de adivinarlo desde las capabilities. Un botón que
 * se esconde por una lista de permisos del cliente miente en cuanto esa lista
 * y el servidor dejan de coincidir.
 *
 * ## Los precios no están aquí
 *
 * El importe y el IVA se ven en la pantalla de pago de Stripe, que es la única
 * que los tiene de verdad. Escribirlos también aquí sería una segunda fuente
 * que se desincroniza en silencio el día que cambie el precio.
 *
 * Los seis estados de `OD-0017 D-03`: CARGANDO, VACÍO (no hay suscripción
 * todavía), CON DATOS, ERROR, SIN PERMISO y LÍMITE ALCANZADO.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api, type EstadoSuscripcion, type PlanDePago, type PruebaDeBusiness, type Suscripcion } from "../api/cliente";
import { esperaCumplida, type Espera } from "./espera-de-plan";
import { olvidarPlanElegido } from "../enlaces/plan-elegido";
import { t } from "../i18n/es";
import { Aviso, Boton, Tarjeta } from "../ui/primitivas";
import { diaLegible } from "../ui/formato";
import { TarjetaDatosFiscales } from "./TarjetaDatosFiscales";
import { TarjetaBajadaAFree } from "./TarjetaBajadaAFree";

/**
 * Mientras el webhook no llega, se vuelve a preguntar. El cron del cobro corre
 * cada minuto, así que un minuto de espera cubre el caso normal; pasado eso se
 * para y se dice que está tardando, en vez de dejar el navegador preguntando
 * para siempre.
 */
const ESPERA_MS = 5000;
const INTENTOS_MAX = 12;

/** Con estos estados ya hay suscripción viva: se gestiona en el portal, no se contrata otra. */
const VIVA: readonly string[] = ["ACTIVE", "PAST_DUE", "CANCELED"];

/*
  Lo que se puede contratar lo dice el SERVIDOR (`contratables`, URS §105).
  Era una lista escrita aqui -Factura+ y Business- y por eso un Business
  puesto a mano, sin suscripcion, veia «Contratar Factura+». Es la misma lista
  que pinta el bloque «Mejora tu plan» del panel: un solo criterio.
*/

type Estado =
  | { readonly tipo: "cargando" }
  | {
      readonly tipo: "datos";
      readonly suscripcion: Suscripcion | null;
      readonly upgrades: readonly PlanDePago[];
      readonly contratables: readonly PlanDePago[];
      /** `OD-0064` D-07: la prueba de Business en curso, o `null`. */
      readonly prueba: PruebaDeBusiness | null;
    }
  | { readonly tipo: "error" }
  | { readonly tipo: "sin-permiso" }
  | { readonly tipo: "limite" };

function fechaLarga(iso: string | null): string | null {
  return iso === null ? null : new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
}

function estadoDe(r: { ok: boolean; estado?: number }, datos: EstadoSuscripcion | null): Estado {
  if (r.ok && datos !== null) {
    return { tipo: "datos", suscripcion: datos.suscripcion, upgrades: datos.upgrades, contratables: datos.contratables, prueba: datos.prueba };
  }
  if (r.estado === 403) return { tipo: "sin-permiso" };
  if (r.estado === 429) return { tipo: "limite" };
  return { tipo: "error" };
}

export function PantallaPlan({
  hayConexion,
  plan,
  vuelta,
  planElegido = null,
  falloInicial = null,
  onPlanCambiado,
}: {
  hayConexion: boolean;
  /** Nombre visible del plan actual ("Plan Gratis"), o `null` si no se sabe. */
  plan: string | null;
  /** De dónde viene el navegador: la URL de vuelta de Stripe. No confirma nada. */
  vuelta: "confirmando" | "cancelado" | null;
  /**
   * El plan elegido en faktelio.com, o `null`. Solo decide QUE BOTON va
   * primero: el pago lo sigue abriendo la API, con sus comprobaciones.
   */
  planElegido?: PlanDePago | null;
  /**
   * Lo que contestó la API cuando el bloque «Mejora tu plan» del panel pidió
   * el pago y no se abrió. Se enseña aquí porque es aquí donde se arregla.
   */
  falloInicial?: string | null;
  /** El plan ya ha cambiado de verdad: quien enseñe su nombre debería releerlo. */
  onPlanCambiado?: () => void;
}) {
  const [estado, setEstado] = useState<Estado>({ tipo: "cargando" });
  /* D3-b: lo dice el servidor. `false` hasta que contesta. */
  const [puedeContratar, setPuedeContratar] = useState(false);
  const anotarPuedeContratar = useCallback((v: boolean) => setPuedeContratar(v), []);
  const [yendoA, setYendoA] = useState<string | null>(null);
  const [fallo, setFallo] = useState<string | null>(falloInicial);
  useEffect(() => {
    if (falloInicial !== null) setFallo(falloInicial);
  }, [falloInicial]);
  /*
    QUÉ se está esperando, no «si» se está esperando.

    Esto era un booleano y estaba mal. La espera terminaba cuando la
    suscripción estaba viva, que es correcto al contratar por primera vez —no
    había y pasa a haber— pero al SUBIR DE PLAN la suscripción ya estaba viva:
    el aviso se apagaba en el mismo instante en que se encendía y no llegaba a
    verse nunca. Pasó el 2026-10-06, con el cobro funcionando por detrás.

    Son dos esperas distintas y cada una termina con su señal:

      SUSCRIPCION   contratar: termina cuando hay una suscripción viva
      PLAN          subir: termina cuando el servidor deja de ofrecer ese plan
                    como posible, que es su forma de decir «ya lo tienes»
  */
  const [espera, setEspera] = useState<Espera | null>(vuelta === "confirmando" ? { hasta: "SUSCRIPCION" } : null);
  const [tarda, setTarda] = useState(false);

  const intentos = useRef(0);

  const cargar = useCallback(async (): Promise<void> => {
    const r = await api.suscripcion();
    setEstado(estadoDe(r, r.ok ? r.datos : null));
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const viva = estado.tipo === "datos" && estado.suscripcion !== null && VIVA.includes(estado.suscripcion.state);

  // Con una suscripcion viva, el plan elegido en el sitio ya no pinta nada.
  useEffect(() => {
    if (viva) olvidarPlanElegido();
  }, [viva]);

  /*
    La espera activa: se vuelve a preguntar hasta que llegue la señal, y como
    mucho INTENTOS_MAX veces, para que una confirmación que nunca llega no deje
    al navegador preguntando indefinidamente.

    Mientras no haya datos no se decide nada: con la pantalla todavía cargando,
    la lista de upgrades está vacía y eso se parecería a «ya lo tienes».
  */
  const cumplida = esperaCumplida(espera, estado.tipo === "datos" ? { viva, upgrades: estado.upgrades } : null);

  useEffect(() => {
    if (espera === null) return;
    if (cumplida) {
      setEspera(null);
      setTarda(false);
      // El nombre del plan lo tiene la pantalla de al lado, que no se entera
      // de esto: sin avisarla, seguiría poniendo el plan viejo hasta que el
      // usuario navegara a otro sitio y volviera.
      onPlanCambiado?.();
      return;
    }
    if (intentos.current >= INTENTOS_MAX) {
      setEspera(null);
      setTarda(true);
      return;
    }
    const id = setTimeout(() => {
      intentos.current += 1;
      void cargar();
    }, ESPERA_MS);
    return () => clearTimeout(id);
  }, [espera, cumplida, estado, cargar, onPlanCambiado]);

  /** Subir de plan teniendo ya suscripción: sin salir a Stripe. */
  async function subir(plan: PlanDePago): Promise<void> {
    setFallo(null);
    setYendoA(plan);
    const r = await api.cambiarPlan(plan);
    setYendoA(null);
    if (!r.ok) {
      setFallo(r.error?.message ?? t.plan.errorAccion);
      return;
    }
    /*
      Stripe ya ha cobrado la diferencia, pero el plan lo sube el webhook. Se
      entra en la misma espera que tras un pago: el contador vuelve a cero
      porque esto es una confirmación nueva, no la de antes.
    */
    intentos.current = 0;
    setTarda(false);
    setEspera({ hasta: "PLAN", objetivo: plan });
    void cargar();
  }

  async function ir(destino: PlanDePago | "portal"): Promise<void> {
    setFallo(null);
    setYendoA(destino);
    const r = destino === "portal" ? await api.portalDePago() : await api.contratar(destino);
    if (r.ok) {
      // Se va a pagar: lo elegido en el sitio ya ha cumplido. Si cancela en
      // Stripe vuelve aqui con los dos botones, que es lo de siempre.
      if (destino !== "portal") olvidarPlanElegido();
      // La URL la da la API y viene de Stripe; el cliente comprueba otra vez a
      // dónde va antes de salir (ver `soloStripe` en api/cliente.ts).
      window.location.href = r.datos.url;
      return;
    }
    setYendoA(null);
    setFallo(r.error?.message ?? t.plan.errorAccion);
  }

  const ocupado = yendoA !== null || !hayConexion;

  /*
    D3-b: contratar exige datos fiscales declarados, y QUIEN LO DECIDE ES EL
    SERVIDOR -`puedeContratar`-. Esta pantalla no compara territorios.

    Arranca en `false` a proposito: hasta que la API contesta, no se sabe, y
    "no se sabe" no habilita nada. El boton deshabilitado no es la proteccion
    -el gate esta en la API, y hay escenarios que lo prueban-: es no invitar a
    un clic que va a fallar.
  */
  /*
    Con un plan elegido en el sitio, ese va primero y como boton principal; el
    otro sigue ahi, en secundario, por si cambia de idea. Sin plan elegido,
    los dos como siempre.
  */
  const contratables: readonly PlanDePago[] = estado.tipo === "datos" ? estado.contratables : [];
  // Un plan elegido que el servidor no ofrece no se pinta: no se podria pagar.
  const elegido = planElegido !== null && contratables.includes(planElegido) ? planElegido : null;
  const orden: readonly PlanDePago[] =
    elegido === null ? contratables : [elegido, ...contratables.filter((p) => p !== elegido)];
  const botonesDeContratar = orden.length === 0 ? (
    <p className="ayuda-neutra">{t.plan.nadaQueContratar}</p>
  ) : (
    <div className="acciones">
      {orden.map((p) => (
        <Boton
          key={p}
          onClick={() => void ir(p)}
          deshabilitado={ocupado || !puedeContratar}
          variante={elegido === null || p === elegido ? "principal" : "secundario"}
        >
          {yendoA === p ? t.plan.abriendoPago : t.plan.contratar[p]}
        </Boton>
      ))}
    </div>
  );

  /*
    «Tu plan» va PRIMERO y los datos de facturacion DEBAJO. Estaba al reves:
    quien entraba a cambiar de plan se encontraba un formulario largo y el
    plan, que es a lo que venia, al fondo de la pagina (auditoria del
    2026-10-19). El orden no cambia nada de lo que se exige: sin datos
    declarados los botones siguen deshabilitados, y la tarjeta del plan dice
    que se rellenan justo debajo.
  */
  return (
    <>
    <Tarjeta titulo={t.plan.titulo}>
      {plan !== null ? (
        <dl className="datos">
          <dt>{t.plan.planActual}</dt>
          <dd>{plan}</dd>
        </dl>
      ) : null}

      {espera !== null ? (
        <Aviso tono="info" titulo={t.plan.confirmandoTitulo}>{t.plan.confirmando}</Aviso>
      ) : null}
      {tarda ? (
        <Aviso tono="aviso" titulo={t.plan.confirmandoTitulo}>{t.plan.confirmandoTarda}</Aviso>
      ) : null}
      {vuelta === "cancelado" ? <Aviso tono="info">{t.plan.cancelado}</Aviso> : null}

      {estado.tipo === "cargando" ? <p>{t.plan.cargando}</p> : null}

      {estado.tipo === "error" ? (
        <>
          <Aviso tono="error">{t.plan.error}</Aviso>
          <Boton variante="secundario" onClick={() => void cargar()}>
            {t.plan.reintentar}
          </Boton>
        </>
      ) : null}

      {estado.tipo === "sin-permiso" ? <Aviso tono="aviso">{t.plan.sinPermiso}</Aviso> : null}
      {estado.tipo === "limite" ? <Aviso tono="aviso">{t.plan.limite}</Aviso> : null}

      {/* VACÍO: no hay ninguna suscripción registrada. Es el caso normal de quien está en Free. */}
      {estado.tipo === "datos" && estado.suscripcion === null ? (
        <>
          {/* OD-0064 D-07: en la prueba, la fecha y lo que pasa si no elige. La seleccion de clientes es la tarjeta de abajo. */}
          {estado.prueba !== null ? (
            <Aviso tono="info" titulo={t.plan.prueba.titulo}>
              {t.plan.prueba.detalle(diaLegible(estado.prueba.ultimoDia))}
            </Aviso>
          ) : null}
          {elegido !== null ? (
            <Aviso tono="info" titulo={t.plan.elegido[elegido]}>{t.plan.elegidoDetalle}</Aviso>
          ) : estado.prueba === null ? (
            <p>{t.plan.sinSuscripcion}</p>
          ) : null}
          <p className="ayuda-neutra">{t.plan.dondeEstaElPrecio}</p>
          {/* Un boton deshabilitado sin explicacion es un boton roto. */}
          {!puedeContratar ? <p className="ayuda-neutra">{t.plan.datosFiscales.rellenaDebajo}</p> : null}
          {botonesDeContratar}
        </>
      ) : null}

      {estado.tipo === "datos" && estado.suscripcion !== null ? (
        <>
          <dl className="datos">
            <dt>{t.plan.estadoSuscripcion}</dt>
            <dd>{t.plan.estados[estado.suscripcion.state]}</dd>
            {fechaLarga(estado.suscripcion.currentPeriodEnd) !== null ? (
              <>
                <dt>{t.plan.hastaCuando}</dt>
                <dd>{fechaLarga(estado.suscripcion.currentPeriodEnd)}</dd>
              </>
            ) : null}
          </dl>
          {viva ? (
            <>
              {/* Los planes a los que se puede subir los decide el SERVIDOR:
                  esta pantalla no sabe cuál está por encima de cuál. */}
              {estado.upgrades.length > 0 ? (
                <>
                  <p className="ayuda-neutra">{t.plan.subirDetalle}</p>
                  <div className="acciones">
                    {estado.upgrades.map((p) => (
                      <Boton key={p} onClick={() => void subir(p)} deshabilitado={ocupado}>
                        {yendoA === p ? t.plan.cambiando : t.plan.subirA[p]}
                      </Boton>
                    ))}
                  </div>
                </>
              ) : null}
              <div className="acciones">
                <Boton variante="secundario" onClick={() => void ir("portal")} deshabilitado={ocupado}>
                  {yendoA === "portal" ? t.plan.abriendoPago : t.plan.gestionar}
                </Boton>
              </div>
            </>
          ) : (
            <>
              <p>{t.plan.terminada}</p>
              {botonesDeContratar}
            </>
          )}
        </>
      ) : null}

      {fallo !== null ? <Aviso tono="error">{fallo}</Aviso> : null}
      {!hayConexion ? <Aviso tono="aviso">{t.plan.sinConexion}</Aviso> : null}
    </Tarjeta>
    {/* La bajada a Free programada, si hay: elegir qué clientes se quedan (TDS-FREE-DOWNGRADE §14). */}
    <TarjetaBajadaAFree hayConexion={hayConexion} />
    <TarjetaDatosFiscales onCambio={anotarPuedeContratar} yaContratado={viva} />
    </>
  );
}
